/**
 * Database Explorer — structured DDL builder contracts.
 *
 * Like rows.*, the client sends intent (column spec, table spec); the route
 * builds the ALTER/CREATE/DROP statement server-side and runs it through the
 * single gated runner (kind='ddl'). Drops require confirmDestructive=true.
 */

import { z } from "zod";
import type { Operation } from "../../types";
import { runResultSchema } from "./sql";

const columnSpec = z.object({
  name: z.string().min(1),
  type: z.string().min(1),
  notnull: z.boolean().optional(),
  default: z.string().optional(),
  pk: z.boolean().optional(),
});
export type ColumnSpec = z.infer<typeof columnSpec>;

export const addColumnBody = z.object({
  table: z.string().min(1),
  column: columnSpec,
});
export const ddlAddColumn: Operation<
  void,
  void,
  z.infer<typeof addColumnBody>,
  z.infer<typeof runResultSchema>
> = {
  method: "POST",
  path: () => "/api/database/ddl/add-column",
  body: addColumnBody,
  response: runResultSchema,
};

export const createTableBody = z.object({
  table: z.string().min(1),
  columns: z.array(columnSpec).min(1),
});
export const ddlCreateTable: Operation<
  void,
  void,
  z.infer<typeof createTableBody>,
  z.infer<typeof runResultSchema>
> = {
  method: "POST",
  path: () => "/api/database/ddl/create-table",
  body: createTableBody,
  response: runResultSchema,
};

export const dropColumnBody = z.object({
  table: z.string().min(1),
  column: z.string().min(1),
  confirm: z.literal(true),
});
export const ddlDropColumn: Operation<
  void,
  void,
  z.infer<typeof dropColumnBody>,
  z.infer<typeof runResultSchema>
> = {
  method: "POST",
  path: () => "/api/database/ddl/drop-column",
  body: dropColumnBody,
  response: runResultSchema,
};

export const dropTableBody = z.object({
  table: z.string().min(1),
  confirm: z.literal(true),
});
export const ddlDropTable: Operation<
  void,
  void,
  z.infer<typeof dropTableBody>,
  z.infer<typeof runResultSchema>
> = {
  method: "POST",
  path: () => "/api/database/ddl/drop-table",
  body: dropTableBody,
  response: runResultSchema,
};

export const addIndexBody = z.object({
  table: z.string().min(1),
  name: z.string().min(1),
  columns: z.array(z.string().min(1)).min(1),
  unique: z.boolean().optional(),
});
export const ddlAddIndex: Operation<
  void,
  void,
  z.infer<typeof addIndexBody>,
  z.infer<typeof runResultSchema>
> = {
  method: "POST",
  path: () => "/api/database/ddl/add-index",
  body: addIndexBody,
  response: runResultSchema,
};

export const ddl = {
  addColumn: ddlAddColumn,
  createTable: ddlCreateTable,
  dropColumn: ddlDropColumn,
  dropTable: ddlDropTable,
  addIndex: ddlAddIndex,
};
